import { EnsureTree, RefreshScheduleItem, RefreshSchedules, TreeReloaded, TreeUpdated } from "event-definitions"
import { GROUP } from "library/constants.mjs"
import { uniqueId } from "library/database/split-id.mjs"
import { Hide, parentFind, parentTreeItem, TreeRoot } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { getTreeIndex } from "routes/schedule/lib/getTreeIndex"
import { DontIndexChildren } from "routes/schedule/lib/getTreeItemIndex"
import { Regime } from "routes/regime/regime"
import { retrieveClientTree } from "routes/schedule/my-schedules/my-custom-schedules/retrieve-client-tree"
import { hasDemand } from "lib/authorization/has-demand"
import { debounce } from "lib/debounce"
import { addDebugItem } from "debug-window"
import { refreshTree } from "ioc"
import { checkPermission } from "library/authorization/index.mjs"

import { MULTIPLE_REGIMES_DEMAND } from "routes/schedule/folder-manager/multiple-regimes-demand"
import { invalidate } from "lib/graphql/cache"
import { ClientChanged } from "library/server-event-definitions/index.mjs"
import { RetrievedTree, RetrievingTree } from "routes/schedule/my-schedules/my-custom-schedules/lib/tree-events"
import { formatRegimeLabel } from "routes/schedule/my-schedules/my-custom-schedules/lib/format-regime-label"
import {
    setDynamicFolderHandling,
    setFolderHandling,
} from "routes/schedule/my-schedules/my-custom-schedules/lib/set-folder-handling"

const treeHandler = {}

TreeUpdated.before.handleOnce((id) => {
    console.log("TREE INVALIDATED", id)
    if (treeHandler[id]) {
        invalidate("groupToSchedules")
        treeHandler[id]()
    }
})

let treeRetrievers = {}
ClientChanged.handleOnce(() => (treeRetrievers = {}))

export function createDynamicTreeRetriever({
    definition,
    tree,
    type = "",
    map = (v) => v,
    folderHandling = true,
    topLevelFolderHandling = folderHandling,
}) {
    let children
    let lastChildren = []
    const { _id: id } = definition
    let loaded = false
    let reading = false
    const treeId = `${type ? `${type}|` : ""}${uniqueId(id.split("|").at(-1))}`
    if (treeRetrievers[treeId]) {
        const cachedRetriever = treeRetrievers[treeId]
        cachedRetriever[parentTreeItem] = tree
        cachedRetriever.users = definition.users
        cachedRetriever.data = definition.data
        cachedRetriever.content = <Regime regime={definition} />
        cachedRetriever.$ = definition
        cachedRetriever.label = formatRegimeLabel(type ? `${type.titleize()}: ` : "", definition?.name)
        return cachedRetriever
    }

    treeHandler[treeId] = debounce(reload, 350)
    const childNode = {
        content: <Regime regime={definition} />,
        id: treeId,
        data: definition.data,
        _id: treeId,
        label: formatRegimeLabel(type ? `${type.titleize()}: ` : "", definition?.name),
        [PlanRoot]: true,
        [TreeRoot]: false,
        [parentTreeItem]: tree,
        [DontIndexChildren]: true,
        visible: true,
        isLoaded: false,
        hasChildren: true,
        type: GROUP,
        hasSubGroups: true,
        users: definition.users,
        [Hide]: !checkPermission(MULTIPLE_REGIMES_DEMAND),
        $: definition,
    }
    setDynamicFolderHandling(childNode, canHandleTopLevelFolders)
    EnsureTree.handle((tree) => {
        if (loaded) return undefined
        if (uniqueId(tree) === uniqueId(treeId)) {
            loaded = true
            return childNode.children
        }
        return undefined
    })
    const retriever = Object.defineProperty(childNode, "children", {
        get() {
            if (children) return children
            if (reading) return lastChildren
            RetrievingTree.raise(treeId)
            reading = true
            retrieveClientTree(
                childNode,
                type,
                treeId,
                map,
                (g) => (childNode.label = formatRegimeLabel(type ? `${type.titleize()}: ` : "", g?.label))
            )
                .then((result) => {
                    setFolderHandling(result, canHandleFolders)
                    children = result
                    lastChildren = result
                    childNode[DontIndexChildren] = false
                    childNode.isLoaded = true
                    getTreeIndex().addToIndex?.(childNode, children)
                    RefreshSchedules.raiseOnceDelay(100)
                    addDebugItem(`${childNode.label} was updated`)
                    const treeRootParent = parentFind(childNode, (c) => c[TreeRoot])
                    RefreshScheduleItem(treeRootParent?.id).raiseOnce()
                })
                .finally(() => {
                    RetrievedTree.raise(treeId)
                    reading = false
                    RefreshScheduleItem(id).raiseOnce()
                    refreshTree()
                    TreeReloaded.raiseOnce()
                })
            return lastChildren
        },
    })

    treeRetrievers[treeId] = retriever
    return retriever

    function reload() {
        if (children) {
            children = null // This MUST happen or it won't reload
            ;({ children } = childNode.children) // This causes it to re-initialize
        }
    }

    function canHandleFolders() {
        return folderHandling && hasDemand("manageRegime")
    }

    function canHandleTopLevelFolders() {
        return topLevelFolderHandling && hasDemand("manageRegime")
    }
}
